import { useState } from "react"
import { useForm } from "react-hook-form"
import { SortDirection, SortField } from "./customTypes"
import { usePostCaseWorkflow } from '../../../openapi/api/endpoints/default/default';
import { queryClient } from "@/App";
import { SetVisibilityRequest } from '../../../openapi/api/model/setVisibilityRequest';
import { UpdateNoteRequest } from '../../../openapi/api/model/updateNoteRequest';
import { Candidate } from '../../../openapi/api/model/candidate';

export function useTableState() {
  const [searchTerm, setSearchTerm] = useState("")
  const [showOnlyQualified, setShowOnlyQualified] = useState(false)
  const [showHidden, setShowHidden] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortBy, setSortBy] = useState<SortField>("category")
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc")
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [showNoteHelper, setShowNoteHelper] = useState(true)

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortBy(field)
      setSortDirection("asc")
    }
  }

  return {
    searchTerm,
    setSearchTerm,
    showOnlyQualified,
    setShowOnlyQualified,
    showHidden,
    setShowHidden,
    currentPage,
    setCurrentPage,
    sortBy,
    sortDirection,
    categoryFilter,
    setCategoryFilter,
    showNoteHelper,
    setShowNoteHelper,
    handleSort,
  }
}

export function useNoteState() {
  const [editingNote, setEditingNote] = useState<string | null>(null)
  const [localNotes, setLocalNotes] = useState<Record<string, string>>({})

  const form = useForm<{ note: string }>({
    defaultValues: { note: "" },
  })

  const handleEditNote = (userId: string, currentNote: string) => {
    setEditingNote(userId)
    form.setValue("note", currentNote)
  }

  const handleSaveNote = (userId: string, onNoteUpdate?: (userId: string, note: string) => void) => {
    const noteText = form.getValues("note")
    setLocalNotes((prev) => ({ ...prev, [userId]: noteText }))
    setEditingNote(null)

    if (onNoteUpdate) {
      onNoteUpdate(userId, noteText)
    }

    console.log("Note saved:", { userId, note: noteText })
  }

  const handleCancelNote = () => {
    setEditingNote(null)
    form.reset()
  }

  return {
    editingNote,
    localNotes,
    form,
    handleEditNote,
    handleSaveNote,
    handleCancelNote,
  }
}

export function useHiddenState() {
  const [localHiddenStates, setLocalHiddenStates] = useState<Record<string, boolean>>({})

  const handleToggleHidden = (
    userId: string,
    currentHiddenState: boolean,
    onCandidateHide?: (userId: string, isHidden: boolean) => void,
  ) => {
    const newHiddenState = !currentHiddenState
    setLocalHiddenStates((prev) => ({ ...prev, [userId]: newHiddenState }))

    if (onCandidateHide) {
      onCandidateHide(userId, newHiddenState)
    }

    console.log("Candidate visibility changed:", { userId, isHidden: newHiddenState })
  }

  return {
    localHiddenStates,
    handleToggleHidden,
  }
}


export const useCaseAction=()=>{
  return usePostCaseWorkflow({
      mutation:{
        onSettled:()=>{
          queryClient.invalidateQueries()
        },
        onMutate: async ({data}) => {
          // Cancel any outgoing refetches
          // (so they don't overwrite our optimistic update)
          const hiringCampaignId=data.hiringCampaignId
          const isInvisible=(data as SetVisibilityRequest).isInvisible
          const note=(data as UpdateNoteRequest).note
  
          await queryClient.cancelQueries({queryKey: [`/hiringCampaigns/${hiringCampaignId}/applicants`]});
  
          // Snapshot the previous value
          const previousApplicantsState = queryClient.getQueryData([`/hiringCampaigns/${hiringCampaignId}/applicants`]) as Candidate[];
          let nextApplicanState:Candidate[]=[]
          switch(data.action){
            case 'setVisibility':
              nextApplicanState=previousApplicantsState.map((candidate)=>{
                if(candidate.case?._id===data.caseId){
                  return {
                    ...candidate,
                    case:{
                      ...candidate.case,
                      isHidden:isInvisible
                    }
  
                  }
                }
                return candidate
              })
              break
            case 'updateNote':
              nextApplicanState=previousApplicantsState.map((candidate)=>{
                if(candidate.case?._id===data.caseId){
                  return {
                    ...candidate,
                    case:{
                      ...candidate.case,
                      note
                    }
  
                  }
                }
                return candidate
              })
              break
            
          }
  
          // Optimistically update to the new value
          queryClient.setQueryData([`/hiringCampaigns/${hiringCampaignId}/applicants`], () => [...nextApplicanState]);
  
          // Return a context object with the snapshotted value
          return { previousApplicantsState };
        },
        onError:(_,{data},context)=>{
          const hiringCampaignId=data.hiringCampaignId
          queryClient.setQueryData([`/hiringCampaigns/${hiringCampaignId}/applicants`], context?.previousApplicantsState)     
        }
      }
    })
}